import { useState, type ReactNode } from "react";
import { Button, Card, StatusIndicator, type StatusTone } from "./ui";
import { SupportLink } from "./support-link";
import type {
  CancellationReview,
  DowngradeAssessment,
  PublicPlanView,
  QuotaResult,
  SafeSubscriptionView,
  SubscriptionState,
  UsageFreshness,
  UsageSummaryItem,
} from "../lib/api";
import { formatCount, formatDateLong } from "../lib/format";

/**
 * Shared billing UI (Phase 9, sections 36–43).
 *
 * HUMANE-BILLING PRINCIPLES enforced here:
 *  - No false urgency: no countdowns, no "limited time", no invented scarcity.
 *  - The FREE plan is never hidden or visually demoted.
 *  - No "Most Popular" badge without a real, server-provided basis (we never render one).
 *  - The most expensive plan is never pre-selected or auto-highlighted.
 *  - Cancellation is explained plainly and routed without obstruction.
 *  - SAFETY: only SAFE server projections are rendered — never a provider price id,
 *    customer id, card/bank detail, secret, or raw internal entitlement flag.
 */

/** A human, calm date. Returns "—" for a missing timestamp (never a fake date). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDateLong(d);
}

export function formatPeriod(period: string | null | undefined): string {
  if (period === "monthly") return "per month";
  if (period === "yearly") return "per year";
  return "";
}

/** Map a normalized subscription state to a calm tone + plain label. */
export function subscriptionTone(status: SubscriptionState): StatusTone {
  switch (status) {
    case "ACTIVE":
    case "TRIALING":
    case "MANUAL_ACTIVE":
      return "ok";
    case "GRACE_PERIOD":
    case "PAST_DUE":
    case "CANCELLING":
      return "warn";
    case "UNPAID":
    case "INCOMPLETE_EXPIRED":
    case "CANCELLED":
    case "MANUAL_EXPIRED":
    case "PAUSED":
      return "danger";
    default:
      return "neutral";
  }
}

export function subscriptionLabel(status: SubscriptionState): string {
  const labels: Record<SubscriptionState, string> = {
    NONE: "No subscription",
    FREE: "Free plan",
    INCOMPLETE: "Setup incomplete",
    INCOMPLETE_EXPIRED: "Setup expired",
    TRIALING: "Trial",
    ACTIVE: "Active",
    PAST_DUE: "Payment past due",
    UNPAID: "Unpaid",
    PAUSED: "Paused",
    CANCELLING: "Cancelling at period end",
    CANCELLED: "Cancelled",
    GRACE_PERIOD: "Grace period",
    MANUAL_ACTIVE: "Active (manual agreement)",
    MANUAL_EXPIRED: "Manual agreement expired",
  };
  return labels[status] ?? status;
}

export function SubscriptionStatusBadge({ status }: { status: SubscriptionState }): JSX.Element {
  return <StatusIndicator tone={subscriptionTone(status)} label={subscriptionLabel(status)} />;
}

/**
 * The public price line for a plan. NEVER invents a price:
 *  - FREE plan             → "Free"
 *  - has a configured price → the server-formatted amount + period
 *  - purchasable, no price   → "Contact us"
 *  - not yet purchasable     → "Not yet publicly available"
 */
export function PlanPriceLine({ plan }: { plan: PublicPlanView }): JSX.Element {
  if (plan.planCode === "FREE") {
    return <p className="kv__v" style={{ fontSize: "1.25rem", fontWeight: 600 }}>Free</p>;
  }
  if (plan.prices.length > 0) {
    return (
      <div style={{ display: "grid", gap: "0.15rem" }}>
        {plan.prices.map((price) => (
          <p key={price.period} style={{ margin: 0 }}>
            <span style={{ fontSize: "1.25rem", fontWeight: 600 }}>{price.formatted}</span>{" "}
            <span className="muted">
              {price.currency} {formatPeriod(price.period)}
            </span>
          </p>
        ))}
      </div>
    );
  }
  if (plan.billingAvailable) {
    return <p className="kv__v" style={{ fontSize: "1.1rem", fontWeight: 600 }}>Contact us</p>;
  }
  return <p className="muted">Not yet publicly available</p>;
}

/**
 * A single plan card. The optional `action` (a CTA the caller controls) and
 * `current` marker let the same card serve the public pricing page and the
 * authenticated billing area. No plan is auto-highlighted by price.
 */
export function PlanCard({
  plan,
  current = false,
  action,
}: {
  plan: PublicPlanView;
  current?: boolean;
  action?: ReactNode;
}): JSX.Element {
  return (
    <Card title={plan.displayName}>
      <div style={{ display: "grid", gap: "0.6rem" }}>
        {current ? <StatusIndicator tone="ok" label="Your current plan" /> : null}
        <PlanPriceLine plan={plan} />
        <p className="muted">{plan.description}</p>

        <div>
          <p className="subhead">Included</p>
          {plan.capabilities.length > 0 ? (
            <ul className="chip-list">
              {plan.capabilities.map((cap) => (
                <li key={cap} className="chip">
                  {cap}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Core features for getting started.</p>
          )}
        </div>

        {plan.primaryLimits.length > 0 ? (
          <div>
            <p className="subhead">Primary limits</p>
            <div className="kv-grid">
              {plan.primaryLimits.map((limit) => (
                <div className="kv" key={limit.label}>
                  <span className="kv__k">{limit.label}</span>
                  <span className="kv__v">{formatCount(limit.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <p className="muted">
          {plan.trialAvailable ? "Trial available." : "No trial for this plan."} {plan.taxNote}
        </p>
        <p className="muted">{plan.cancellationSummary}</p>
        <p className="muted">
          Availability:{" "}
          {plan.billingAvailable ? "Available now." : "Not yet available for self-service purchase."}
        </p>
        {action ? <div className="row" style={{ marginTop: "0.25rem" }}>{action}</div> : null}
      </div>
    </Card>
  );
}

/**
 * A calm banner for a payment issue (past due / unpaid). It explains what failed,
 * what continues, what may pause, and how to fix it — with NO alarmist countdown
 * and NO deletion threat.
 */
export function PaymentFailureBanner({
  status,
  onOpenPortal,
  portalPending = false,
}: {
  status: SubscriptionState;
  onOpenPortal?: () => void;
  portalPending?: boolean;
}): JSX.Element {
  return (
    <div className="banner-item banner-item--warn" role="status">
      <div className="banner-item__message" style={{ display: "grid", gap: "0.35rem" }}>
        <strong className="banner-item__label">
          {status === "UNPAID" ? "A payment could not be collected" : "Your last payment did not go through"}
        </strong>
        <p style={{ margin: 0 }}>
          Your plan is still active for now. Your community, bots and settings keep running while we
          retry. If the payment cannot be collected, premium features move to read-only and your plan
          returns to Free — nothing in Discord is deleted automatically, and you can always export your
          data first.
        </p>
        <p style={{ margin: 0 }} className="muted">
          To fix this, update your payment method in the secure customer portal. We never handle or store
          card details in this dashboard.
        </p>
        {/* Part 10.2 — failed-payment support. Deliberately AFTER the self-service fix,
            so nobody is pushed into a queue for something they can solve in a minute. */}
        <p style={{ margin: 0 }} className="muted">
          Already updated it, or the card is fine?{" "}
          <SupportLink topic="PAYMENT_FAILURE">Message support</SupportLink> and we will look at it from
          our side.
        </p>
      </div>
      {onOpenPortal ? (
        <Button variant="secondary" onClick={onOpenPortal} disabled={portalPending}>
          {portalPending ? "Opening…" : "Update payment method"}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Grace-period banner. Explains what continues, what may pause, when the window
 * ends (if known — shown as a plain date, never a live ticking countdown), and the
 * safe ways forward: update payment, export, or move to Free.
 */
export function GraceBanner({
  graceEndsIso,
  onOpenPortal,
  portalPending = false,
}: {
  graceEndsIso: string | null;
  onOpenPortal?: () => void;
  portalPending?: boolean;
}): JSX.Element {
  return (
    <div className="banner-item banner-item--warn" role="status">
      <div className="banner-item__message" style={{ display: "grid", gap: "0.35rem" }}>
        <strong className="banner-item__label">You are in a grace period</strong>
        <p style={{ margin: 0 }}>
          While billing is being sorted out, your plan keeps working so nothing is disrupted. Safety and
          billing tools stay available throughout.
          {graceEndsIso ? ` This grace period is scheduled to end on ${formatDate(graceEndsIso)}.` : ""}
        </p>
        <p style={{ margin: 0 }} className="muted">
          After the grace period, premium features become read-only until payment is restored. You can
          update your payment method, export your data at any time, or move to the Free plan — no data is
          deleted for you automatically.
        </p>
        {/* Part 10.2 — failed-payment support. A grace period is exactly the moment a
            customer needs a person, and the desk records the conversation against
            their account instead of leaving them to compose an email into silence. */}
        <p style={{ margin: 0 }} className="muted">
          If the payment keeps failing or you think this is wrong,{" "}
          <SupportLink topic="PAYMENT_FAILURE">message support</SupportLink> — a person will look at it
          with you.
        </p>
      </div>
      {onOpenPortal ? (
        <Button variant="secondary" onClick={onOpenPortal} disabled={portalPending}>
          {portalPending ? "Opening…" : "Manage payment"}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Cancellation review + the in-app Cancel Plan action (Part 7.4 / 7.5).
 *
 * Everything the decision needs is on screen BEFORE anything happens: when the plan
 * stops, what is kept, what pauses, and how to come back. Cancelling then takes one
 * plainly labelled confirmation — there is no retention survey, no discount wall, no
 * "are you sure you want to lose…", no typed phrase, no phone call and no email
 * requirement, and the action is never hidden or visually demoted below the routes
 * that keep the customer paying. Reversing a cancellation is exactly one click, so
 * cancelling is never made the harder of the two.
 *
 * The panel only ever reports what the SERVER did: if the payment provider cannot
 * schedule the cancellation, it says so and points at the hosted portal rather than
 * claiming a cancellation that never happened.
 */
export function CancellationReviewPanel({
  review,
  onOpenPortal,
  portalPending = false,
  canCancel = true,
  onCancelPlan,
  cancelPending = false,
  cancelError = null,
  cancelNotice = null,
}: {
  review: CancellationReview;
  onOpenPortal?: () => void;
  portalPending?: boolean;
  canCancel?: boolean;
  onCancelPlan?: () => void;
  cancelPending?: boolean;
  cancelError?: string | null;
  cancelNotice?: string | null;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const cancelHere = onCancelPlan !== undefined && canCancel && review.canCancel;
  const activeUntil = formatDate(review.accessRemainsUntilIso ?? review.effectiveAtIso);

  return (
    <Card title="Cancelling your plan">
      <div style={{ display: "grid", gap: "0.6rem" }}>
        <div className="kv-grid">
          <div className="kv">
            <span className="kv__k">Current plan</span>
            <span className="kv__v">{review.currentPlan}</span>
          </div>
          {review.priceDisplay ? (
            <div className="kv">
              <span className="kv__k">Price today</span>
              <span className="kv__v">{review.priceDisplay}</span>
            </div>
          ) : null}
          <div className="kv">
            <span className="kv__k">Paid through</span>
            <span className="kv__v">{formatDate(review.paidThroughIso)}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Stays active until</span>
            <span className="kv__v">{activeUntil}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Then moves to</span>
            <span className="kv__v">{review.downgradesTo}</span>
          </div>
        </div>
        {review.priceDisplay ? <p className="muted">{review.vatNote}</p> : null}

        <div>
          <p className="subhead">What happens</p>
          <ul className="ax-confirm__list">
            {review.effects.map((effect) => (
              <li key={effect} className="muted">
                {effect}
              </li>
            ))}
          </ul>
        </div>

        {review.dataRetention.length > 0 ? (
          <div>
            <p className="subhead">What ODIN keeps</p>
            <ul className="ax-confirm__list">
              {review.dataRetention.map((line) => (
                <li key={line} className="muted">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="muted">{review.reactivation}</p>
        <p className="muted">{review.note}</p>
        {/* Part 10.2 — cancellation support. Offered ALONGSIDE the cancel action, never
            in front of it: making somebody talk to a person before they may leave is
            the retention gauntlet Part 7.5 rules out. Cancelling stays one click. */}
        <p className="muted">
          Not sure, or cancelling because something is wrong?{" "}
          <SupportLink topic="CANCEL_PLAN">Message support</SupportLink> — this does not delay or
          replace cancelling, which you can still do right here.
        </p>

        {confirming ? (
          <section className="ax-confirm" aria-label="Confirm cancelling your plan">
            <h4 className="ax-confirm__title">Cancel your {review.currentPlan} plan?</h4>
            <p className="ax-confirm__subtitle">
              Your plan keeps working until {activeUntil}, then this workspace moves to{" "}
              {review.downgradesTo}. Nothing is deleted, and you can call this off at any time before
              that date.
            </p>
            <p className="ax-metric__foot">
              This stops the next renewal. It is not a refund of the period you have already paid for.
            </p>
            {cancelError ? (
              <p className="ax-confirm__error" role="alert">
                {cancelError}
              </p>
            ) : null}
            <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
              <button
                type="button"
                className="ax-btn ax-btn--danger"
                onClick={onCancelPlan}
                disabled={cancelPending}
              >
                {cancelPending ? "Cancelling…" : "Cancel my plan"}
              </button>
              <button
                type="button"
                className="ax-btn ax-btn--secondary"
                onClick={() => setConfirming(false)}
                disabled={cancelPending}
              >
                Keep my plan
              </button>
            </div>
          </section>
        ) : null}

        <div className="row" style={{ gap: "0.75rem" }}>
          {cancelHere && !confirming ? (
            <Button variant="secondary" onClick={() => setConfirming(true)} disabled={cancelPending}>
              Cancel plan
            </Button>
          ) : null}
          {onOpenPortal ? (
            <Button
              variant={cancelHere ? "ghost" : "secondary"}
              onClick={onOpenPortal}
              disabled={portalPending}
            >
              {portalPending ? "Opening…" : "Continue to customer portal"}
            </Button>
          ) : null}
        </div>

        {cancelNotice ? (
          <p className="muted" role="status">
            {cancelNotice}
          </p>
        ) : null}
        {cancelError && !confirming ? (
          <p className="ax-confirm__error" role="alert">
            {cancelError}
          </p>
        ) : null}

        {!cancelHere ? (
          <p className="muted">
            {review.alreadyScheduled
              ? "This plan is already scheduled to stop renewing. You can call that off from your plan panel above, at any time before the end date."
              : canCancel && review.canCancel
                ? "You can finish cancelling on the secure customer portal."
                : "You can review this here. Confirming a cancellation is limited to an owner or billing admin."}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

// ── Usage helpers ───────────────────────────────────────────────────────────

/** Human category + label for each internal usage metric (no provider/cost secrets). */
export const USAGE_METRIC_META: Readonly<
  Record<string, { category: string; label: string; unit?: "bytes" }>
> = {
  "balder.requests": { category: "BALDER", label: "BALDER requests" },
  "balder.input_tokens": { category: "BALDER", label: "BALDER input tokens" },
  "balder.output_tokens": { category: "BALDER", label: "BALDER output tokens" },
  "balder.tool_calls": { category: "BALDER", label: "BALDER tool calls" },
  "workflow.executions": { category: "Automation", label: "Workflow executions" },
  "integration.polls": { category: "Integrations", label: "Integration polls" },
  "notification.deliveries": { category: "Notifications", label: "Notification deliveries" },
  "connected.guilds": { category: "Guilds & bots", label: "Connected guilds" },
  "bot.instances": { category: "Guilds & bots", label: "Bot instances" },
  "storage.bytes": { category: "Storage", label: "Storage used", unit: "bytes" },
  "exports.created": { category: "Exports", label: "Exports created" },
  "game_server.polls": { category: "Integrations", label: "Game server polls" },
  "rss.items_ingested": { category: "Integrations", label: "RSS items ingested" },
  "stream.events_ingested": { category: "Integrations", label: "Stream events ingested" },
};

export function metricMeta(metric: string): { category: string; label: string; unit?: "bytes" } {
  return USAGE_METRIC_META[metric] ?? { category: "Other", label: metric };
}

/** Is this reading a still-moving estimate, or a finalized value? */
export function isFinalUsage(freshness: UsageFreshness): boolean {
  return freshness === "FINAL" || freshness === "CORRECTED";
}

export function freshnessLabel(freshness: UsageFreshness): string {
  switch (freshness) {
    case "FINAL":
      return "Final";
    case "CORRECTED":
      return "Final (corrected)";
    case "FINALIZING":
      return "Finalizing";
    case "ESTIMATED":
      return "Estimated";
    default:
      return "Estimated (live)";
  }
}

function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return String(bytes);
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function formatUsageValue(value: number, unit?: "bytes"): string {
  return unit === "bytes" ? formatBytesShort(value) : formatCount(value);
}

export function quotaTone(quota: QuotaResult): StatusTone {
  if (quota.state === "limit-reached") return "danger";
  if (quota.state === "warning") return "warn";
  return "ok";
}

/** One usage row: allowance, consumption, remaining, warning threshold, reset,
 * freshness (estimated vs final), and the latest update. No cost/provider data. */
export function UsageMetricRow({ item }: { item: UsageSummaryItem }): JSX.Element {
  const meta = metricMeta(item.metric);
  const q = item.quota;
  // BALDER AI usage is always metered against a defined per-plan allowance and is
  // never presented as "Unlimited" — even if an allowance ever arrives null, we
  // show a neutral "Metered" label rather than implying unrestricted AI usage.
  const isBalder = meta.category === "BALDER";
  const unlimited = q.allowance === null && !isBalder;
  const meteredNoCap = q.allowance === null && isBalder;
  const final = isFinalUsage(q.freshness);
  return (
    <Card title={meta.label}>
      <div style={{ display: "grid", gap: "0.5rem" }}>
        <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <span className="chip">{meta.category}</span>
          <StatusIndicator tone={quotaTone(q)} label={
            q.state === "limit-reached" ? "Limit reached" : q.state === "warning" ? "Approaching limit" : "Within limit"
          } />
          <span className={`count-chip ${final ? "count-chip--ok" : "count-chip--warn"}`}>
            {final ? "Final" : "Estimated"}
          </span>
        </div>
        <div className="kv-grid">
          <div className="kv">
            <span className="kv__k">Allowance</span>
            <span className="kv__v">{meteredNoCap ? "Metered" : unlimited ? "Unlimited" : formatUsageValue(q.allowance ?? 0, meta.unit)}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Used this period</span>
            <span className="kv__v">{formatUsageValue(q.consumed, meta.unit)}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Remaining</span>
            <span className="kv__v">
              {meteredNoCap ? "Metered" : unlimited || q.remaining === null ? "Unlimited" : formatUsageValue(q.remaining, meta.unit)}
            </span>
          </div>
          <div className="kv">
            <span className="kv__k">Warning at</span>
            <span className="kv__v">{Math.round(q.warnThreshold * 100)}% of allowance</span>
          </div>
          <div className="kv">
            <span className="kv__k">Resets</span>
            <span className="kv__v">
              {q.resetEpochSec ? formatDate(new Date(q.resetEpochSec * 1000).toISOString()) : "Start of next period"}
            </span>
          </div>
          <div className="kv">
            <span className="kv__k">Reading</span>
            <span className="kv__v">{freshnessLabel(q.freshness)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

/** Plan-change / downgrade impact list from a plan-change-review assessment. */
export function PlanChangeImpacts({ assessment }: { assessment: DowngradeAssessment }): JSX.Element {
  const impactful = assessment.impacts.filter((i) => i.kind !== "no-impact");
  return (
    <div style={{ display: "grid", gap: "0.5rem" }}>
      <p className="muted">{assessment.effectiveNote}</p>
      {impactful.length === 0 ? (
        <p className="muted">No features are reduced by this change.</p>
      ) : (
        <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
          {impactful.map((impact) => (
            <li key={impact.key} className="muted">
              <strong>{impact.key}</strong>: {impact.explanation}
            </li>
          ))}
        </ul>
      )}
      {assessment.requiresManualChoice ? (
        <p className="muted">
          Some resources exceed the target plan. Nothing is deleted — you choose how to adjust before or
          after changing plans.
        </p>
      ) : null}
    </div>
  );
}

/**
 * A compact summary of the current subscription for the billing overview (Part 7.1).
 *
 * Shows the plan, its VAT-inclusive price, what has already been paid for, when the
 * next payment is taken, and whether a payment method is on file — PRESENCE ONLY.
 * ODIN never receives a card brand, last four digits, expiry or fingerprint, so none
 * of those can be rendered here even by accident. The price is omitted rather than
 * guessed whenever the server could not quote it accurately.
 */
export function SubscriptionSummary({ subscription }: { subscription: SafeSubscriptionView }): JSX.Element {
  const priceLine = subscription.priceDisplay
    ? `${subscription.priceDisplay}${subscription.priceCadenceLabel ? ` ${subscription.priceCadenceLabel}` : ""}`
    : subscription.planCode === "FREE"
      ? "Free"
      : "—";
  const scheduled =
    subscription.scheduledChange !== null ||
    subscription.cancelAtPeriodEnd ||
    subscription.cancellationRequested;

  return (
    <div style={{ display: "grid", gap: "0.5rem" }}>
      <div className="kv-grid">
        <div className="kv">
          <span className="kv__k">Plan</span>
          <span className="kv__v">{subscription.planCode}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Status</span>
          <span className="kv__v">
            <SubscriptionStatusBadge status={subscription.status} />
          </span>
        </div>
        <div className="kv">
          <span className="kv__k">Price</span>
          <span className="kv__v">{priceLine}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Billing period</span>
          <span className="kv__v">{subscription.billingPeriod ?? "—"}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Paid through</span>
          <span className="kv__v">{formatDate(subscription.paidThroughIso)}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Next payment</span>
          <span className="kv__v">
            {subscription.renews ? formatDate(subscription.nextBillingIso) : "No further payments"}
          </span>
        </div>
        <div className="kv">
          <span className="kv__k">Trial ends</span>
          <span className="kv__v">{formatDate(subscription.trialEndIso)}</span>
        </div>
        <div className="kv">
          <span className="kv__k">Payment method</span>
          <span className="kv__v">
            {subscription.paymentMethodOnFile ? "Held by our payment provider" : "Not on file"}
          </span>
        </div>
        <div className="kv">
          <span className="kv__k">Cancellation</span>
          <span className="kv__v">{scheduled ? "Scheduled at period end" : "Not scheduled"}</span>
        </div>
      </div>
      {subscription.priceDisplay ? <p className="muted">{subscription.vatNote}</p> : null}
      <p className="muted">{subscription.paymentMethodNote}</p>
    </div>
  );
}
